import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BriefcaseIcon,
  StarIcon,
  ArrowLongRightIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

import BrandForm from 'components/Register/BrandForm';
import PromoterForm from 'components/Register/PromoterForm';
import { Hero, SuccessView } from 'components/Register/primitives';
import { useModal } from 'context/ModalContext';
import { peekPostAuthRedirect } from 'utils/postAuthRedirect';

const RegistrationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showLoginModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = searchParams.get('role');
  const [submittedEmail, setSubmittedEmail] = useState(null);

  const setRole = (next) => {
    if (!next) {
      const sp = new URLSearchParams(searchParams);
      sp.delete('role');
      setSearchParams(sp);
    } else {
      setSearchParams({ role: next });
    }
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
  };

  const handleSuccess = (email) => {
    setSubmittedEmail(email || '');
    if (typeof window !== 'undefined') window.scrollTo({ top: 0 });
  };

  // Invited brands go back to the event they were invited to. Only peeked:
  // the confirm-email page still needs it if they confirm from the email.
  const handleContinueAfterSuccess = () => {
    const next = peekPostAuthRedirect();
    navigate(next || '/');
    showLoginModal(next);
  };

  if (role !== 'brand' && role !== 'promoter') {
    return <RoleChooser onPick={setRole} />;
  }

  const isBrand = role === 'brand';

  if (submittedEmail !== null) {
    return (
      <div className="min-h-screen bg-paper-bg">
        <Hero
          crumbTrail={[
            { label: t('register.crumb_signup', 'Sign up') },
            { label: isBrand ? t('register.brand_tab') : t('register.promoter_tab') },
            { label: t('register.success_crumb', 'Almost there') },
          ]}
          title={t('register.success_hero_title', 'Almost\nthere.')}
        />
        <SuccessView email={submittedEmail} onContinue={handleContinueAfterSuccess} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-bg">
      <Hero
        crumbTrail={[
          { label: t('register.crumb_signup', 'Sign up') },
          { label: isBrand ? t('register.brand_tab') : t('register.promoter_tab') },
        ]}
        title={
          isBrand
            ? t('register.brand_hero_title', 'Become a\nbrand.')
            : t('register.promoter_hero_title', 'Become a\npromoter.')
        }
        lede={
          isBrand
            ? t('register.brand_hero_lede')
            : t('register.promoter_hero_lede')
        }
        meta={
          isBrand
            ? [
                t('register.step_count', 'Step 2 of 2'),
                { ok: t('register.auto_saving', 'Auto-saving') },
              ]
            : [t('register.step_count', 'Step 2 of 2')]
        }
      />

      <div className="mx-auto w-full max-w-[1200px] px-5 pb-16 pt-10 lg:px-14 lg:pb-24 lg:pt-12">
        {isBrand ? (
          <BrandForm onSuccess={handleSuccess} />
        ) : (
          <PromoterForm onSuccess={handleSuccess} />
        )}

        <p className="mt-12 text-center text-sm text-muted">
          {t('register.switch_role', 'Wrong role?')}{' '}
          <button
            type="button"
            onClick={() => setRole(null)}
            className="font-semibold text-ink underline underline-offset-2 hover:text-ink/70"
          >
            {t('register.go_back', 'Go back')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegistrationPage;

const RoleChooser = ({ onPick }) => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-paper-bg">
      <Hero
        crumbTrail={[{ label: t('register.crumb_signup', 'Sign up') }]}
        title={t('register.title_v2', 'Join\nthere2b.')}
        lede={t('register.subtitle_v2')}
        wideLede
        meta={[t('register.free_to_join', 'Free to join')]}
      />

      <div className="mx-auto grid w-full max-w-[960px] grid-cols-1 gap-5 px-5 pb-20 pt-14 sm:px-8 md:grid-cols-2 lg:gap-6 lg:px-14 lg:pb-32">
        <RoleCard
          grey
          icon={<BriefcaseIcon className="h-6 w-6" strokeWidth={1.7} />}
          forLabel={t('register.brand_for')}
          title={t('register.brand_title')}
          bullets={[
            t('register.brand_bullet_1'),
            t('register.brand_bullet_2'),
            t('register.brand_bullet_3'),
          ]}
          cta={t('register.brand_cta')}
          onClick={() => onPick('brand')}
        />
        <RoleCard
          icon={<StarIcon className="h-6 w-6" strokeWidth={1.7} />}
          forLabel={t('register.promoter_for')}
          title={t('register.promoter_title')}
          bullets={[
            t('register.promoter_bullet_1'),
            t('register.promoter_bullet_2'),
            t('register.promoter_bullet_3'),
          ]}
          cta={t('register.promoter_cta')}
          onClick={() => onPick('promoter')}
        />
      </div>
    </div>
  );
};

const RoleCard = ({
  grey,
  icon,
  forLabel,
  title,
  bullets,
  cta,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex flex-col gap-4 rounded-[20px] border p-7 text-left transition hover:-translate-y-[3px] hover:shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-ink ${
      grey
        ? 'border-line-strong bg-gradient-to-b from-paper-2 to-white hover:border-muted'
        : 'border-line bg-white hover:border-brand'
    }`}
  >
    <span
      className={`grid h-14 w-14 place-items-center rounded-[14px] ${
        grey
          ? 'bg-ink text-white'
          : 'bg-brand text-ink shadow-[0_8px_18px_-10px_rgba(255,189,89,0.6)]'
      }`}
    >
      {icon}
    </span>

    <span className="flex min-h-[34px] items-start font-display text-[11px] font-bold uppercase leading-[1.5] tracking-[0.16em] text-muted">
      {forLabel}
    </span>

    <h3 className="text-[26px] font-extrabold leading-[1.1] tracking-[-0.02em] text-ink-strong sm:text-[30px]">
      {title}
    </h3>

    <ul className="flex flex-col gap-2.5">
      {bullets.map((b, i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm leading-[1.45] text-ink-2">
          <CheckIcon className="mt-[3px] h-3.5 w-3.5 shrink-0 text-ink" strokeWidth={2.4} />
          <span>{b}</span>
        </li>
      ))}
    </ul>

    <div className="mt-auto flex justify-end pt-3">
      <span
        className={`inline-flex items-center gap-2 rounded-[10px] border px-4 py-3 text-sm font-semibold ${
          grey
            ? 'border-ink bg-ink text-white'
            : 'border-brand bg-brand text-ink'
        }`}
      >
        {cta}
        <ArrowLongRightIcon className="h-4 w-4" strokeWidth={2} />
      </span>
    </div>
  </button>
);
